import { ColumnDef } from '@tanstack/react-table'
import { Calendar, CheckCircle, XmarkCircle } from 'iconoir-react'
import { DateComponent } from 'app/components/DateComponent'
import { PageScroll } from 'app/components/layout/PageScroll'
import { PageHeader } from 'app/components/ui/page-header'
import TableLayout from 'app/components/table/TableLayout'
import { OpenLinkCell } from 'app/components/table/OpenLinkCell'
import { TableEmptyState } from 'app/components/ui/table-empty-state'
import { LocationJson } from 'app/types/entities'
import { SchedulingSubmissions } from './useSchedulingIndex'

export type SchedulingIndexShadcnComponentProps = {
  locations: {
    hits: LocationJson[]
    total: number
  }
  columns: ColumnDef<LocationJson, any>[]
}

export function getSchedulingShadcnColumns(submissions: SchedulingSubmissions): ColumnDef<LocationJson, any>[] {
  return [
    {
      id: `Location Name`,
      accessorKey: `name`,
      header: `Location Name`,
      meta: {
        defaultVisible: true,
        pinned: true,
        sortKey: `name.keyword`,
        // A controlled cell fills the whole cell, so the hover Open button sits at its right edge.
        ControlledComponent: (row) => (
          <OpenLinkCell
            href={`/settings/scheduling/${row?.id}`}
            label={row?.name ?? ``}
            disabled={!row?.products?.includes(`Scheduler`)}
          />
        ),
      },
    },
    {
      id: `Scheduling Enabled`,
      header: `Scheduling Enabled`,
      cell: (props) => (
        <span>
          {props.row.original.products?.includes(`Scheduler`) ? (
            <CheckCircle className="h-5 w-5 text-blue-500" />
          ) : (
            <XmarkCircle className="h-5 w-5 text-red-400" />
          )}
        </span>
      ),
      meta: {
        defaultVisible: true,
      },
      enableSorting: false,
    },
    {
      id: `Submissions`,
      header: `Submissions`,
      enableSorting: true,
      cell: (props) => <span>{submissions[props.row.original.id!]?.count ?? 0}</span>,
      meta: {
        defaultVisible: true,
      },
    },
    {
      id: `Last Submission`,
      header: `Last Submission`,
      enableSorting: true,
      cell: (props) => DateComponent({ value: submissions[props.row.original.id!]?.last, dateOnly: true }),
      meta: {
        defaultVisible: true,
      },
    },
  ]
}

export function SchedulingIndexShadcnComponent({ locations, columns }: SchedulingIndexShadcnComponentProps) {
  return (
    <PageScroll variant="table" header={<PageHeader title="Online Scheduler" totalItems={locations?.total} />}>
      <TableLayout
        data={locations}
        columns={columns as any}
        edgePadded
        emptyState={
          <TableEmptyState
            icon={<Calendar className="h-6 w-6" strokeWidth={1.5} />}
            title="No locations yet"
            description="Every location shows up here, along with whether online scheduling is turned on and how many people have booked through it."
          />
        }
      />
    </PageScroll>
  )
}
